import {
	ASSIGN_USERS_OPTION,
	AssignmentDraft,
	AssignmentSetupType,
	AssignUsersOptionField,
	ConfigureState,
	CustomSetupState,
	DEFAULT_DRAFT,
	SelectedGroupOption,
	SelectedRoleOption,
	SelectedUserOption,
	Step,
	getStepsByType,
	getLimitedEditSteps
} from 'LearningManager/constants/createAssignment.constant';
import {
	assignmentActivityIds,
	assignmentLearningPathIds
} from 'LearningManager/helpers/assignment';
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useStores } from 'Hooks/useStore';
import { AssignmentSource } from 'Services/Api/AssignmentSources/Types';
import { DirectoryGroup } from 'Services/Api/Directory/Types';
import useTenantScope from 'LearningManager/hooks/useTenantScope';
import {
	readAssignmentDraft,
	resolveResumableStep,
	writeAssignmentDraft
} from 'LearningManager/hooks/useAssignmentDraft';

// Re-exported so consumers keep importing the wizard's data shapes from the
// context they are used through.
export type {
	AssignmentDraft,
	ConfigureState,
	SelectedGroupOption,
	SelectedRoleOption,
	SelectedUserOption
};

// Maps the scope key the API returns to the Assign Users option id ('groups' is
// shared by Organization/Property/Group, so it can only resolve to Group).
const scopeKeyToAssignUsersOptionId = (
	scopes: AssignmentSource['scopes']
): number | null => {
	if (scopes.includes('roles')) return ASSIGN_USERS_OPTION.ROLE;
	if (scopes.includes('users')) return ASSIGN_USERS_OPTION.INDIVIDUAL;
	if (scopes.includes('group_labels')) return ASSIGN_USERS_OPTION.ORGANIZATION;
	if (scopes.includes('groups')) return ASSIGN_USERS_OPTION.GROUP;
	return null;
};

type GroupSelectionField =
	| 'selectedOrganizations'
	| 'selectedProperties'
	| 'selectedGroups';

const groupLabelAudience = (
	label?: string
): { optionId: number; field: GroupSelectionField } => {
	if (label === 'Organization') {
		return {
			optionId: ASSIGN_USERS_OPTION.ORGANIZATION,
			field: 'selectedOrganizations'
		};
	}
	if (label === 'Property') {
		return {
			optionId: ASSIGN_USERS_OPTION.PROPERTY,
			field: 'selectedProperties'
		};
	}
	return { optionId: ASSIGN_USERS_OPTION.GROUP, field: 'selectedGroups' };
};

const groupScopeAudience = (
	groupIds: number[],
	groups: DirectoryGroup[]
): Partial<CustomSetupState> => {
	const labels = [...new Set(groups.map((g) => g.label))];
	const target = groupLabelAudience(
		labels.length === 1 ? labels[0] : undefined
	);
	const groupsById = new Map(groups.map((g) => [g.id, g]));

	const audience: Partial<CustomSetupState> = {
		selectedAssignUsersOptionId: target.optionId
	};
	audience[target.field] = groupIds.map((groupId) => {
		const group = groupsById.get(groupId);
		return {
			id: groupId,
			name: group?.name ?? `Group ${groupId}`,
			label: group?.label
		};
	});
	if (target.field === 'selectedGroups') {
		audience.selectedGroupPropertyIds = [
			...new Set(groups.map((g) => g.parentId))
		];
	}
	return audience;
};

const ALL_PROPERTIES_FLAG = {
	selectedRolePropertyIds: 'allRolePropertiesSelected',
	selectedGroupPropertyIds: 'allGroupPropertiesSelected',
	selectedIndividualPropertyIds: 'allIndividualPropertiesSelected'
} as const;

type PropertyFilterField = keyof typeof ALL_PROPERTIES_FLAG;

const OPTIONAL_COURSE_FIELD = {
	selectedLearningPathCourseIds: 'optionalLearningPathIds',
	selectedIndividualCourseIds: 'optionalIndividualCourseIds'
} as const;

type CustomCourseField = keyof typeof OPTIONAL_COURSE_FIELD;

const withOptionalActivities = (
	optionalIds: number[],
	activityIds: number[],
	isOptional: boolean
): number[] => {
	const remaining = optionalIds.filter((id) => !activityIds.includes(id));
	return isOptional ? [...remaining, ...activityIds] : remaining;
};

const mapAssignmentSourceToDraft = (
	source: AssignmentSource
): AssignmentDraft => {
	const certDuration =
		source.expiration_type === 'interval'
			? 'one-year'
			: source.expiration_type === 'date'
			? 'custom'
			: 'never';

	// expiration_date is stored as 'MM-DD'; rebuild a parseable date for the picker
	const certExpirationDate =
		source.expiration_type === 'date' && source.expiration_date
			? `${moment().format('YYYY')}-${source.expiration_date}`
			: '';

	const isFutureStart = source.start_date
		? moment(source.start_date).isAfter(moment(), 'day')
		: false;

	const learningPathIds = assignmentLearningPathIds(source.learning_paths);
	const activityIds = assignmentActivityIds(source.activities);

	return {
		configure: {
			assignmentName: source.name,
			certDuration,
			certExpirationDate,
			assignmentStart: isFutureStart ? 'schedule' : 'immediate',
			assignmentStartDate: source.start_date ?? '',
			dueDate: source.due_date ? 'custom' : 'none',
			dueDateValue: source.due_date ?? '',
			assignmentType: source.is_assigned_to_future_users
				? 'ongoing-assignment'
				: 'one-time-assignment'
		},
		quickSetup: {
			selectedLearningPathCourseIds: learningPathIds
		},
		customSetup: {
			...DEFAULT_DRAFT.customSetup,
			selectedLearningPathCourseIds: learningPathIds,
			selectedIndividualCourseIds: activityIds,
			optionalLearningPathIds: assignmentLearningPathIds(
				source.learning_paths.filter((lp) => !lp.is_required)
			),
			optionalIndividualCourseIds: assignmentActivityIds(
				source.activities.filter((a) => !a.is_required)
			),
			selectedAssignUsersOptionId: scopeKeyToAssignUsersOptionId(
				source.scopes
			)
		}
	};
};

interface StepperContextValue {
	current: number;
	steps: Step[];
	totalSteps: number;
	isFirst: boolean;
	isLast: boolean;
	setupType: AssignmentSetupType;
	isEditMode: boolean;
	isLimitedEdit: boolean;
	// True on the setup-selection screen, which sits before either flow's steps.
	isSetupSelection: boolean;
	draft: AssignmentDraft;
	assignmentTenantId?: number;
	isAssignmentLoading: boolean;
	setSetupType: React.Dispatch<React.SetStateAction<AssignmentSetupType>>;
	updateDraft: (updater: (prev: AssignmentDraft) => AssignmentDraft) => void;
	updateConfigure: (data: Partial<ConfigureState>) => void;
	toggleQuickCourseSelection: (id: number) => void;
	toggleCustomCourseSelection: (field: CustomCourseField, id: number) => void;
	setCustomCourseRequired: (
		field: CustomCourseField,
		id: number,
		isRequired: boolean
	) => void;
	toggleAssignUsersSelection: (
		field: AssignUsersOptionField,
		item: SelectedGroupOption | SelectedRoleOption
	) => void;
	setAssignUsersSelection: (
		field: AssignUsersOptionField,
		items: (SelectedGroupOption | SelectedRoleOption)[]
	) => void;
	setAssignUsersPropertyFilter: (
		field: PropertyFilterField,
		ids: number[],
		allSelected?: boolean
	) => void;
	setSelectedAssignUsersOptionId: (id: number) => void;
	toggleIndividualUserSelection: (user: SelectedUserOption) => void;
	setIndividualUserSelection: (users: SelectedUserOption[]) => void;
	next: () => void;
	back: () => void;
	goTo: (step: number) => void;
}

const StepperContext = createContext<StepperContextValue | null>(null);

interface StepperProviderProps {
	children: React.ReactNode;
}

export const StepperProvider: React.FC<StepperProviderProps> = observer(({
	children
}) => {
	const { search } = useLocation();
	const history = useHistory();
	const {
		learningManagerStore,
		authStore,
		assignUsersStore,
		systemsStore
	} = useStores();
	const { canSelectTenant, globalTenantId } = useTenantScope();
	const query = useMemo(() => new URLSearchParams(search), [search]);
	const currentSetupType = query.get(
		'setupType'
	) as AssignmentSetupType | null;
	const assignmentId = query.get('assignmentId');
	const isEditMode = Boolean(assignmentId || query.get('mode') === 'edit');
	const isLimitedEdit =
		Boolean(assignmentId) && query.get('isScheduled') !== 'true';
	// The setup type only reaches the URL once the selection screen is left.
	const isSetupSelection = !currentSetupType;

	const [current, setCurrent] = useState(0);
	const [setupType, setSetupType] = useState<AssignmentSetupType>(
		currentSetupType ?? 'Quick Setup'
	);
	const [draft, setDraft] = useState<AssignmentDraft>(DEFAULT_DRAFT);
	const [assignmentTenantId, setAssignmentTenantId] = useState<
		number | undefined
	>();
	const [isAssignmentLoading, setIsAssignmentLoading] = useState(
		Boolean(assignmentId)
	);
	const steps = isLimitedEdit
		? getLimitedEditSteps(setupType)
		: getStepsByType(setupType);
	const totalSteps = steps.length;

	// Create flow only — in edit mode the server copy is the source of truth.
	const isDraftEnabled = !isEditMode;

	// A draft belongs to one tenant: the one a super admin selected in the
	// sidebar, or the user's own.
	const draftTenantId = canSelectTenant
		? globalTenantId
			? String(globalTenantId)
			: ''
		: String(authStore.currentUser?.userPool?.zeeNumber ?? '');

	// The sidebar selection isn't kept across a reload, so a super admin may
	// arrive with none; their draft's tenant is then taken.
	const sessionTenantId =
		canSelectTenant && !globalTenantId ? null : draftTenantId;

	const persistDraft = (
		nextStep: number,
		nextSetupType: AssignmentSetupType,
		nextDraft: AssignmentDraft
	) => {
		if (!isDraftEnabled) return;
		writeAssignmentDraft({
			tenantId: draftTenantId,
			tenantName: canSelectTenant
				? systemsStore.selectedTenant.label
				: undefined,
			flowType: nextSetupType,
			currentStep: nextStep,
			formData: nextDraft
		});
	};

	const hasHydratedRef = useRef(false);
	// Holds a just-restored flow type until the URL catches up, so the reset
	// effect can tell that apart from the user choosing a setup type.
	const restoredFlowRef = useRef<AssignmentSetupType | null>(null);

	// Declared before hydration so the mount pass clamps against the pre-restore
	// step list; the restored step is clamped once `setupType` catches up.
	useEffect(() => {
		setCurrent((prev) => Math.min(prev, totalSteps - 1));
	}, [totalSteps]);

	useEffect(() => {
		if (hasHydratedRef.current || !isDraftEnabled) return;
		// Which tenant a draft has to match isn't knowable until the user is.
		if (!authStore.currentUser) return;

		hasHydratedRef.current = true;

		const stored = readAssignmentDraft({
			currentTenantId: sessionTenantId,
			expectedFlowType: currentSetupType
		});
		if (!stored) return;

		restoredFlowRef.current = stored.flowType;
		setSetupType(stored.flowType);
		setDraft(stored.formData);
		setCurrent(resolveResumableStep(stored));
		if (canSelectTenant && !globalTenantId && stored.tenantId) {
			const tenantId = Number(stored.tenantId);
			systemsStore.changeTenant({
				label:
					systemsStore.tenantList.find((t) => t.value === tenantId)
						?.label ??
					stored.tenantName ??
					'',
				value: tenantId
			});
		}

		// Without the setup type in the URL the flow renders Select Courses.
		if (currentSetupType !== stored.flowType) {
			const params = new URLSearchParams(search);
			params.set('setupType', stored.flowType);
			history.replace({ search: params.toString() });
		}
		// Runs once, as soon as the user (and so the tenant) is known.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authStore.currentUser, isDraftEnabled]);

	useEffect(() => {
		// The URL is only now catching up to a restored draft — resetting here
		// would throw that restored state away.
		if (restoredFlowRef.current) {
			if (currentSetupType === restoredFlowRef.current) {
				setSetupType(currentSetupType);
				restoredFlowRef.current = null;
			}
			return;
		}

		if (currentSetupType) {
			setSetupType(currentSetupType);
		}
		setCurrent(0);

		if (!assignmentId) {
			setAssignmentTenantId(undefined);
			setIsAssignmentLoading(false);
			setDraft(DEFAULT_DRAFT);
			// Entering or switching flows starts a fresh draft, but only after
			// hydration has had its turn at reading a resumable one.
			if (currentSetupType && hasHydratedRef.current) {
				persistDraft(0, currentSetupType, DEFAULT_DRAFT);
			}
			return;
		}

		// Edit mode: prefill the draft from the existing assignment source
		let cancelled = false;
		const id = Number(assignmentId);
		setIsAssignmentLoading(true);
		learningManagerStore
			.getAssignmentSource(id)
			.then(async (source) => {
				if (cancelled) return;
				setDraft(mapAssignmentSourceToDraft(source));
				// Editing keeps the assignment on its own tenant.
				const sourceTenantId = Number(source.tenant_id);
				const editedTenantId = Number.isFinite(sourceTenantId)
					? sourceTenantId
					: undefined;
				setAssignmentTenantId(editedTenantId);
				setIsAssignmentLoading(false);

				// The assignment-source response carries only the scope keys, so
				// the selections come from the scope detail endpoints.
				const audience: Partial<CustomSetupState> = {};
				try {
					if (source.scopes.includes('roles')) {
						const scopeRoles = await learningManagerStore.getAssignmentSourceScopeRoles(
							id
						);
						if (!assignUsersStore.roles.length) {
							await assignUsersStore
								.fetchRoles()
								.catch(() => undefined);
						}
						const roleNames = new Map(
							assignUsersStore.roles.map((r) => [r.id, r.name])
						);
						audience.selectedRoles = scopeRoles.map((r) => ({
							id: r.id,
							name: roleNames.get(r.id) ?? `Role ${r.id}`
						}));
					}
					if (source.scopes.includes('groups')) {
						const scopeGroups = await learningManagerStore.getAssignmentSourceScopeGroups(
							id
						);
						const groupIds = scopeGroups.map((g) => g.id);
						const groups = await assignUsersStore
							.fetchGroupsByIds(groupIds)
							.catch(() => []);
						Object.assign(
							audience,
							groupScopeAudience(groupIds, groups)
						);
					}
					if (
						source.scopes.includes('group_labels') &&
						editedTenantId !== undefined
					) {
						const labels = await learningManagerStore.getAssignmentSourceScopeGroupLabels(
							id
						);
						if (labels.some((l) => l.group_id === 'Organization')) {
							const [organization] = await assignUsersStore
								.fetchGroupsByIds([editedTenantId])
								.catch(() => []);
							audience.selectedOrganizations = [
								{
									id: editedTenantId,
									name:
										organization?.name ??
										`Organization ${editedTenantId}`
								}
							];
						}
					}
					if (source.scopes.includes('users')) {
						const scopeUsers = await learningManagerStore.getAssignmentSourceScopeUsers(
							id
						);
						await assignUsersStore.fetchUsersByIds(
							scopeUsers.map((u) => u.id)
						);
						audience.selectedIndividualUsers = assignUsersStore.assignmentUsers.map(
							(u) => ({
								id: u.id,
								firstName: u.firstName,
								lastName: u.lastName,
								email: u.email
							})
						);
					}
				} catch {
					// leave selections empty if scope resolution fails
					return;
				}

				if (!cancelled && Object.keys(audience).length) {
					setDraft((prev) => ({
						...prev,
						customSetup: { ...prev.customSetup, ...audience }
					}));
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAssignmentTenantId(undefined);
					setIsAssignmentLoading(false);
					setDraft(DEFAULT_DRAFT);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [currentSetupType, assignmentId]);

	// Every step change re-saves the draft — a cheaper checkpoint than saving on
	// each keystroke, and the stored step always matches what is on screen.
	const moveTo = (step: number) => {
		const target = Math.max(0, Math.min(step, totalSteps - 1));
		setCurrent(target);
		persistDraft(target, setupType, draft);
	};
	const next = () => moveTo(current + 1);
	const back = () => moveTo(current - 1);
	const goTo = (step: number) => {
		if (step >= 0 && step < totalSteps) moveTo(step);
	};
	const updateDraft = (updater: (prev: AssignmentDraft) => AssignmentDraft) =>
		setDraft((prev) => updater(prev));
	const updateConfigure = (data: Partial<ConfigureState>) =>
		setDraft((prev) => ({
			...prev,
			configure: { ...prev.configure, ...data }
		}));
	const toggleQuickCourseSelection = (id: number) => {
		setDraft((prev) => {
			const selectedIds = prev.quickSetup.selectedLearningPathCourseIds;
			const nextIds = selectedIds.includes(id)
				? selectedIds.filter((item) => item !== id)
				: [...selectedIds, id];
			return {
				...prev,
				quickSetup: {
					...prev.quickSetup,
					selectedLearningPathCourseIds: nextIds
				}
			};
		});
	};
	const learningPathActivityIds = (
		field: CustomCourseField,
		id: number
	): number[] =>
		field === 'selectedLearningPathCourseIds'
			? (
					learningManagerStore.learningPaths.find((lp) => lp.id === id)
						?.activities ?? []
			  ).map((activity) => activity.activity_id)
			: [];
	const toggleCustomCourseSelection = (
		field: CustomCourseField,
		id: number
	) => {
		const optionalField = OPTIONAL_COURSE_FIELD[field];
		const activityIds = learningPathActivityIds(field, id);
		setDraft((prev) => {
			const selectedIds = prev.customSetup[field];
			const isSelecting = !selectedIds.includes(id);
			const nextIds = isSelecting
				? [...selectedIds, id]
				: selectedIds.filter((item) => item !== id);
			const selectedIndividualCourseIds = isSelecting
				? prev.customSetup.selectedIndividualCourseIds.filter(
						(item) => !activityIds.includes(item)
				  )
				: prev.customSetup.selectedIndividualCourseIds;
			const optionalIndividualCourseIds = prev.customSetup[
				optionalField
			].includes(id)
				? withOptionalActivities(
						prev.customSetup.optionalIndividualCourseIds,
						activityIds,
						false
				  )
				: prev.customSetup.optionalIndividualCourseIds;
			return {
				...prev,
				customSetup: {
					...prev.customSetup,
					optionalIndividualCourseIds,
					selectedIndividualCourseIds,
					[field]: nextIds,
					[optionalField]: prev.customSetup[optionalField].filter(
						(item) => item !== id
					)
				}
			};
		});
	};
	const setCustomCourseRequired = (
		field: CustomCourseField,
		id: number,
		isRequired: boolean
	) => {
		const optionalField = OPTIONAL_COURSE_FIELD[field];
		const activityIds = learningPathActivityIds(field, id);
		setDraft((prev) => {
			const customSetup = {
				...prev.customSetup,
				optionalIndividualCourseIds: withOptionalActivities(
					prev.customSetup.optionalIndividualCourseIds,
					activityIds,
					!isRequired
				)
			};
			const optionalIds = customSetup[optionalField].filter(
				(item) => item !== id
			);
			return {
				...prev,
				customSetup: {
					...customSetup,
					[optionalField]: isRequired
						? optionalIds
						: [...optionalIds, id]
				}
			};
		});
	};

	const setSelectedAssignUsersOptionId = (id: number) => {
		let updatedState : any = {};
		if(id === ASSIGN_USERS_OPTION.ORGANIZATION) {
			updatedState = { selectedOrganizations: [] };
		} else if(id === ASSIGN_USERS_OPTION.PROPERTY) {
			updatedState = { selectedProperties: [] };
		} else if(id === ASSIGN_USERS_OPTION.ROLE) {
			updatedState = { selectedRoles: [], selectedRolePropertyIds: [], allRolePropertiesSelected: false };
		} else if(id === ASSIGN_USERS_OPTION.GROUP) {
			updatedState = { selectedGroups: [], selectedGroupPropertyIds: [], allGroupPropertiesSelected: false };
		} else if(id === ASSIGN_USERS_OPTION.INDIVIDUAL) {
			updatedState = { selectedIndividualUsers: [], selectedIndividualPropertyIds: [], allIndividualPropertiesSelected: false };
		}
		setDraft((prev) => ({
			...prev,
			customSetup: { ...prev.customSetup, selectedAssignUsersOptionId: id, ...updatedState }
		}));
	}

	const toggleAssignUsersSelection = (
		field: AssignUsersOptionField,
		item: SelectedGroupOption | SelectedRoleOption
	) => {
		setDraft((prev) => {
			const selected = prev.customSetup[field] as Array<{ id: number }>;
			const nextItems = selected.some((s) => s.id === item.id)
				? selected.filter((s) => s.id !== item.id)
				: [...selected, item];
			return {
				...prev,
				customSetup: {
					...prev.customSetup,
					[field]: nextItems
				}
			};
		});
	};
	const setAssignUsersSelection = (
		field: AssignUsersOptionField,
		items: (SelectedGroupOption | SelectedRoleOption)[]
	) => {
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				[field]: items
			}
		}));
	};
	const setAssignUsersPropertyFilter = (
		field: PropertyFilterField,
		ids: number[],
		allSelected = false
	) => {
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				[field]: allSelected ? [] : ids,
				[ALL_PROPERTIES_FLAG[field]]: allSelected
			}
		}));
	};

	const toggleIndividualUserSelection = (user: SelectedUserOption) => {
		setDraft((prev) => {
			const current = prev.customSetup.selectedIndividualUsers;
			const next = current.some((u) => u.id === user.id)
				? current.filter((u) => u.id !== user.id)
				: [...current, user];
			return {
				...prev,
				customSetup: { ...prev.customSetup, selectedIndividualUsers: next }
			};
		});
	};

	const setIndividualUserSelection = (users: SelectedUserOption[]) => {
		setDraft((prev) => ({
			...prev,
			customSetup: { ...prev.customSetup, selectedIndividualUsers: users }
		}));
	};

	return (
		<StepperContext.Provider
			value={{
				current,
				steps,
				totalSteps,
				isFirst: current === 0,
				isLast: current === totalSteps - 1,
				setupType,
				isEditMode,
				isLimitedEdit,
				isSetupSelection,
				draft,
				assignmentTenantId,
				isAssignmentLoading,
				setSetupType,
				updateDraft,
				updateConfigure,
				toggleQuickCourseSelection,
				toggleCustomCourseSelection,
				setCustomCourseRequired,
				toggleAssignUsersSelection,
				setAssignUsersSelection,
				setAssignUsersPropertyFilter,
				setSelectedAssignUsersOptionId,
				toggleIndividualUserSelection,
				setIndividualUserSelection,
				next,
				back,
				goTo
			}}
		>
			{children}
		</StepperContext.Provider>
	);
});

export const useStepper = (): StepperContextValue => {
	const context = useContext(StepperContext);
	if (!context) {
		throw new Error('useStepper must be used within a StepperProvider');
	}
	return context;
};
